import { readonly, ref } from 'vue'
import type { Ref } from 'vue'

export interface UseInfiniteScrollOptions {
  /** Carrega a próxima página de dados. Erros são relançados ao caller. */
  loadMore: () => Promise<void>
  /** Indica se ainda existem mais registros a carregar. */
  hasMore: () => boolean
  /** Indica se há outra operação em andamento (ex.: load inicial). Default: `() => false`. */
  isBusy?: () => boolean
  /** Tempo (ms) de cooldown entre disparos consecutivos. Default: `800`. */
  cooldownMs?: number
}

export interface UseInfiniteScrollReturn {
  /** `true` enquanto `loadMore` está em andamento — use para exibir o spinner de "carregando mais". */
  isLoadingMore: Readonly<Ref<boolean>>
  /** Handler para a diretiva `v-intersect` do elemento sentinela. */
  onIntersect: (isIntersecting: boolean) => Promise<void>
  /** Limpa lock/cooldown e zera `isLoadingMore` — chamar ao trocar filtro/empresa. */
  reset: () => void
}

/**
 * Infinite scroll genérico para listas paginadas com `v-intersect` (Vuetify).
 *
 * Centraliza o padrão repetido no repo: lock de reentrância + cooldown entre
 * disparos, evitando chamadas duplicadas quando o sentinela permanece visível.
 *
 * Uso típico:
 * ```vue
 * <div v-if="hasMore && !isLoadingMore" v-intersect="onIntersect" class="h-8" />
 * ```
 * ```ts
 * const { isLoadingMore, onIntersect, reset } = useInfiniteScroll({
 *   loadMore: () => store.fetchNextPage(),
 *   hasMore: () => store.hasMore,
 *   isBusy: () => store.isLoading
 * })
 * // ao trocar filtro/empresa: reset()
 * ```
 *
 * Comportamento:
 * - `onIntersect(false)` é no-op.
 * - `onIntersect(true)` é no-op se não há mais páginas (`hasMore`), se há outra
 *   operação em andamento (`isBusy`), se já está carregando ou se está em cooldown.
 * - Erros de `loadMore` são relançados (o caller trata); o estado nunca fica travado.
 */
export function useInfiniteScroll(options: UseInfiniteScrollOptions): UseInfiniteScrollReturn {
  const { loadMore, hasMore, isBusy = () => false, cooldownMs = 800 } = options

  const isLoadingMore = ref(false)
  let isCoolingDown = false
  let cooldownTimeout: ReturnType<typeof setTimeout> | null = null

  async function onIntersect(isIntersecting: boolean): Promise<void> {
    if (!isIntersecting) return
    if (!hasMore() || isBusy() || isLoadingMore.value || isCoolingDown) return

    isCoolingDown = true
    isLoadingMore.value = true
    try {
      await loadMore()
    } finally {
      isLoadingMore.value = false
      cooldownTimeout = setTimeout(() => {
        isCoolingDown = false
        cooldownTimeout = null
      }, cooldownMs)
    }
  }

  function reset(): void {
    if (cooldownTimeout !== null) {
      clearTimeout(cooldownTimeout)
      cooldownTimeout = null
    }
    isCoolingDown = false
    isLoadingMore.value = false
  }

  return { isLoadingMore: readonly(isLoadingMore), onIntersect, reset }
}
